/**
 * ConsoleKpis — the v2 console's four stats, each one a way in (D-7).
 *
 * Classic showed five read-only pills. Two of them named work nobody could
 * reach from them (Drafts waiting, Escalations) and two named numbers whose
 * explanation lived behind an admin tab most people never opened. v2 keeps the
 * exact same pill — same border, same type scale, same phone swipe-strip → `sm`
 * card behavior — and makes the ones with somewhere to go into buttons. The
 * REST state is byte-identical to classic on purpose: the strip has to read the
 * same at a glance, so the affordance is a hover ring and a focus ring, not a
 * new shape.
 *
 * "Needs you" is the SAME variable the tab badge renders — it is passed in, never
 * recomputed here (see needsYou.ts for why that rule exists).
 *
 * Auto-sent unedited and Spend today land on admin-only surfaces (the weekly
 * review, the tuning copilot), so for everyone else they stay plain stats rather
 * than buttons that 403.
 */
import type { ReactNode } from 'react';
import { useIsCRMAdmin } from '../../../lib';
import type { Metrics } from '../AgentConsole';

/**
 * One metric. On phones it's a compact inline pill in a single swipeable strip;
 * from `sm` up it's the card it has always been. `onClick` adds the button
 * affordance without touching the rest state.
 */
function Stat({
  label, value, title, onClick, ariaLabel,
}: {
  label: string;
  value: string;
  title?: string;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const shell =
    'flex flex-shrink-0 items-baseline gap-1.5 whitespace-nowrap rounded-full border border-gray-200 bg-white px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800 sm:block sm:rounded-xl sm:py-2';
  const inner: ReactNode = (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">{label}</div>
      <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100 sm:mt-0.5 sm:text-lg">{value}</div>
    </>
  );

  if (!onClick) return <div title={title} className={shell}>{inner}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className={`${shell} text-left transition-shadow duration-150 hover:ring-2 hover:ring-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:ring-primary-900/50`}
    >
      {inner}
    </button>
  );
}

export interface ConsoleKpisProps {
  metrics: Metrics | null;
  /** THE Needs-you number (needsYou.ts) — the same one the tab badge shows. */
  needsYouCount: number;
  /** A source list came back full: render "N+". */
  needsYouCapped: boolean;
  onGoConversations: () => void;
  onGoNeeds: () => void;
  /** Manage → Review (admins only; the tile is a plain stat otherwise). */
  onGoReview: () => void;
  /** Manage → Tuning (admins only). */
  onGoTuning: () => void;
}

export default function ConsoleKpis({
  metrics, needsYouCount, needsYouCapped, onGoConversations, onGoNeeds, onGoReview, onGoTuning,
}: ConsoleKpisProps) {
  const isAdmin = useIsCRMAdmin();
  const needsValue = needsYouCapped ? `${needsYouCount}+` : String(needsYouCount);

  return (
    <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 [-webkit-overflow-scrolling:touch] sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-x-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
      <Stat
        label="Active"
        value={String(metrics?.active_conversations ?? '—')}
        title="Conversations the agent is running right now — open the board"
        ariaLabel={`Active conversations: ${metrics?.active_conversations ?? 'unknown'}. Open the Conversations tab`}
        onClick={onGoConversations}
      />
      <Stat
        label="Needs you"
        value={needsValue}
        title="Determinations, drafts and agent issues waiting on a person"
        ariaLabel={`Needs you: ${needsValue}. Open the Needs you tab`}
        onClick={onGoNeeds}
      />
      <Stat
        label="Auto-sent unedited"
        value={metrics?.approval_untouched_rate == null ? '—' : `${metrics.approval_untouched_rate}%`}
        title={isAdmin
          ? 'Share of approved replies sent exactly as the agent wrote them — open the weekly review'
          : 'Share of approved replies sent exactly as the agent wrote them'}
        ariaLabel={isAdmin
          ? `Auto-sent unedited: ${metrics?.approval_untouched_rate ?? 'unknown'} percent. Open the weekly review`
          : undefined}
        onClick={isAdmin ? onGoReview : undefined}
      />
      <Stat
        label="Spend today"
        value={metrics ? `$${metrics.spend_today_usd.toFixed(2)}` : '—'}
        title={isAdmin ? "Today's agent model spend — open tuning" : "Today's agent model spend"}
        ariaLabel={isAdmin
          ? `Spend today: ${metrics ? `$${metrics.spend_today_usd.toFixed(2)}` : 'unknown'}. Open tuning`
          : undefined}
        onClick={isAdmin ? onGoTuning : undefined}
      />
    </div>
  );
}
